import type { BlockMobileScreenshots } from 'types/block-mobile-screenshots';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';

import { Button, Paragraph, Wrap } from '@leafwell/components';

import HeadingBody from './heading-body';
import Figure from './figure';
import { useEventsTracker } from 'contexts/eventsTracker';
import { toQueryString } from 'lib/utils/toQueryString';

const MobileScreenshots: React.FC<BlockMobileScreenshots> = ({
  sectionId,
  instanceId,
  title,
  description,
  list,
  button,
}) => {
  const { trackEvent } = useEventsTracker();

  const { asPath, query } = useRouter();
  const posthog = usePostHog();
  const sessionId = posthog.get_session_id();
  const distinctId = posthog.get_distinct_id();

  const product: 'membership' | 'telepharmacist' | null = asPath.includes(
    '/membership',
  )
    ? 'membership'
    : asPath.includes('/cannabis-care')
    ? 'telepharmacist'
    : null;
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const queryParams = toQueryString(query);

  useEffect(() => {
    if (product) {
      setNewUrl(
        button?.url?.includes('?')
          ? `${button.url}&session_id=${sessionId}&distinct_id=${distinctId}${
              queryParams ? `&${queryParams}` : ''
            }`
          : `${button.url}?session_id=${sessionId}&distinct_id=${distinctId}${
              queryParams ? `&${queryParams}` : ''
            }`,
      );
    }
  }, [sessionId, distinctId, product, queryParams]);

  const colorArray = [
    'bg-[#FFE194]',
    'bg-[#EAE1F8]',
    'bg-green-50',
    'bg-[#FFCCC6]',
  ];
  return (
    <section
      id={sectionId}
      className="bg-lilac-50 my-12 md:my-16"
      itemID={`MobileScreenshots-${instanceId}`}
    >
      <Wrap>
        {title || description ? (
          <div className="grid gap-y-6 justify-items-center">
            {title && (
              <HeadingBody
                level="2"
                tagName="h2"
                title={title}
                className="font-semibold text-center"
              />
            )}
            {description && (
              <HeadingBody
                level="6"
                tagName="p"
                title={description}
                className="text-center"
              />
            )}
          </div>
        ) : null}
        {list?.length > 0 ? (
          <ol className="flex flex-col xl:flex-row justify-center items-center xl:items-start gap-x-4 md:gap-x-16 gap-y-20 min-w-max">
            {list?.map(({ title, description, image }, idx) => {
              return (
                <li
                  key={`${sectionId}-${idx}`}
                  className={['relative flex flex-col gap-8 w-72'].join(' ')}
                >
                  {image && (
                    <Figure
                      {...image}
                      className="grid items-end justify-center z-10"
                      classNameImage="w-44"
                    />
                  )}
                  <span
                    className={[
                      'absolute w-72 h-72 top-8 left-0 rounded-full',
                      list?.length < 5 ? colorArray[idx] : colorArray[0],
                    ].join(' ')}
                  ></span>
                  <div className="flex flex-col gap-y-2">
                    <HeadingBody
                      tagName="p"
                      level="5"
                      title={`${idx + 1}.`}
                      className="font-semibold text-center"
                    />
                    {title?.length > 0 && (
                      <HeadingBody
                        tagName="p"
                        level="6"
                        title={title}
                        className="text-center"
                      />
                    )}
                    {description?.length > 0 && (
                      <Paragraph
                        tagName="div"
                        className="text-center"
                        dangerouslySetInnerHTML={{ __html: description }}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        ) : null}
        {button ? (
          <Button
            label={button.title}
            href={product ? newUrl : button.url}
            rounded={true}
            variant="secondary"
            className="w-fit h-fit justify-self-center"
            icon="arrow"
            onClick={() => {
              trackEvent({
                event: 'select_content',
                content_type: button.title,
                item_id: `MobileScreenshots-${instanceId}`,
                action: 'MobileScreenshots-click-cta',
              });
              if (product) {
                trackEvent({
                  event: 'generate_lead',
                  item_id: `MobileScreenshots-${instanceId}`,
                  service: product,
                  page_referrer: document.referrer,
                });
              }
            }}
          />
        ) : null}
      </Wrap>
    </section>
  );
};

export default MobileScreenshots;
